import { useQuery } from '@tanstack/react-query';
import { Checkbox, Label } from '@/components/ui';
import { spaceApi } from '@/services/api';
import type { AwsSessionTag } from '@/types/identity';

/**
 * Agents of an arbitrary space (not the route's active space — the
 * credentials page can manage any space the user admins). Used to pick which
 * agent(s) a trust policy pins (`optr-agent-id`) and which agent to simulate.
 */
export function useAgentsOfSpace(spaceId: string | undefined) {
  return useQuery({
    queryKey: ['space', spaceId, 'agents'],
    queryFn: () => spaceApi(spaceId!).agents.list(),
    enabled: !!spaceId,
  });
}

/** Radix Select can't carry an empty value — sentinel for "none". */
export const NONE = '__none__';

/** The `optr-*` STS session tags identity sets on every AssumeRole. */
export const AWS_SESSION_TAG_HELP: Array<{ key: string; description: string }> = [
  { key: 'optr-org-id', description: 'Your OPTR organization (always set; also the ExternalId)' },
  { key: 'optr-space-id', description: 'The space the agent is running in' },
  { key: 'optr-agent-id', description: 'The agent making the call' },
  { key: 'optr-run-id', description: 'The individual agent run (audit / CloudTrail)' },
];

/** Multi-select of a space's agents as checkboxes. */
export function AgentPicker({
  spaceId,
  selected,
  onChange,
}: {
  spaceId: string | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const { data, isLoading } = useAgentsOfSpace(spaceId);
  const agents = data?.items ?? [];
  if (!spaceId) {
    return <p className="text-xs text-muted-foreground">Pick a space to choose its agents.</p>;
  }
  if (isLoading) return <p className="text-xs text-muted-foreground">Loading agents…</p>;
  if (agents.length === 0) {
    return <p className="text-xs text-muted-foreground">This space has no agents yet.</p>;
  }
  return (
    <div className="max-h-40 overflow-y-auto space-y-1.5 rounded-md border border-border p-2">
      {agents.map((a) => {
        const id = `agent-${a.agentId}`;
        const checked = selected.includes(a.agentId);
        return (
          <div key={a.agentId} className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={checked}
              onCheckedChange={(v) =>
                onChange(v ? [...selected, a.agentId] : selected.filter((x) => x !== a.agentId))
              }
            />
            <Label htmlFor={id} className="text-sm font-normal">
              {a.name} <span className="text-xs text-muted-foreground">{a.agentId}</span>
            </Label>
          </div>
        );
      })}
    </div>
  );
}

/** Result panel for a simulated AssumeRole: outcome + the exact tags sent. */
export function AwsRoleTestResult({
  ok,
  callerArn,
  message,
  reason,
  simulatedTags,
}: {
  ok: boolean;
  callerArn?: string;
  message?: string;
  reason?: string;
  simulatedTags?: AwsSessionTag[];
}) {
  return (
    <div
      className={
        ok
          ? 'rounded-md border border-status-success-fg/30 bg-status-success-bg/30 p-3 space-y-2'
          : 'rounded-md border border-status-error-fg/30 bg-status-error-bg/30 p-3 space-y-2'
      }
      role={ok ? 'status' : 'alert'}
    >
      <p className="text-sm font-medium text-foreground">
        {ok ? 'AssumeRole succeeded' : 'AssumeRole was rejected'}
      </p>
      {ok && callerArn && <p className="text-xs text-muted-foreground break-all">{callerArn}</p>}
      {!ok && (
        <p className="text-xs text-muted-foreground break-words">
          {message ?? reason ?? 'Unknown error'}
          {reason === 'aws_role_trust_rejected' &&
            ' — check that the role trust policy trusts the OPTR broker role and matches the tags below.'}
        </p>
      )}
      {simulatedTags && simulatedTags.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="font-normal pr-3">Session tag sent</th>
              <th className="font-normal">Value</th>
            </tr>
          </thead>
          <tbody>
            {simulatedTags.map((t) => (
              <tr key={t.Key}>
                <td className="pr-3 font-mono">{t.Key}</td>
                <td className="font-mono break-all">{t.Value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
